import * as React from 'react';
import cx from 'classnames';
import Coin from 'components/Coin';
import LayerTrigger from 'components/LayerTrigger';
import Tooltip from 'components/Tooltip';
import Glyph, { GlyphColor } from 'components/Glyph';
import getPropClasses from 'lib/getPropClasses';
import Icon from 'components/Icon';
import Image from 'components/Image';
import { CoinColor } from 'components/Coin';
import { GlyphName } from '@remind/r101-glyphs';
import { IconName } from 'components/Icon';

import './styles/Avatar.scss';

const SHORT_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

type AvatarBadgeProps = {
  backgroundColor: CoinColor;
  iconColor: GlyphColor;
  iconName: GlyphName;
  tooltipCopy?: string | null;
};

export function AvatarBadge(props: AvatarBadgeProps) {
  return props.tooltipCopy ? (
    <Coin className="Avatar-badge" color={props.backgroundColor} size="xxxsmall">
      <LayerTrigger layer={<Tooltip>{props.tooltipCopy}</Tooltip>} placement="top">
        <Glyph name={props.iconName} color={props.iconColor} size="xxxsmall" solo />
      </LayerTrigger>
    </Coin>
  ) : (
    <Coin className="Avatar-badge" color={props.backgroundColor} size="xxxsmall">
      <Glyph name={props.iconName} color={props.iconColor} size="xxxsmall" solo />
    </Coin>
  );
}

type AvatarContentProps = {
  date?: Date;
  glyphName?: GlyphName;
  iconName?: IconName;
  image?: string;
  paddedImage?: string;
  text?: string;
  glyphSize: 'small' | 'large';
  iconSize: 'small' | 'large';
};

function AvatarContent(props: AvatarContentProps) {
  const { date, glyphName, iconName, image, text, glyphSize, iconSize, paddedImage } = props;

  if (image) {
    return <Image src={image} className="Avatar-image" />;
  }

  if (paddedImage) {
    return <Image src={paddedImage} className="Avatar-paddedImage" />;
  }

  if (iconName) {
    return <Icon name={iconName} className="Avatar-icon" size={iconSize} />;
  }

  if (glyphName) {
    return (
      <div className="Avatar-glyphContainer">
        {glyphName && (
          <div>
            <Glyph name={glyphName} size={glyphSize} solo className="Avatar-glyph" />
          </div>
        )}
        {text && <div className="Avatar-text">{text}</div>}
      </div>
    );
  }

  if (date) {
    return (
      <div className="Avatar-dateContainer">
        <div className="Avatar-dateContainer-month">{SHORT_MONTH_NAMES[date.getMonth()]}</div>
        <div className="Avatar-dateContainer-day">{date.getDate()}</div>
      </div>
    );
  }

  if (text) {
    return <span>{text}</span>;
  }

  return <span />;
}

export type AvatarSize = 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall';
export type AvatarColor = 'brand' | 'grey' | 'fullGrey' | 'white' | 'coachingBlue' | 'default';

type AvatarProps = {
  color?: AvatarColor;
  date?: Date;
  iconName?: IconName;
  glyphName?: GlyphName;
  // use `image` if you want Avatar to make the image full width
  // and then crop it into a circle
  image?: string;
  // use `paddedImage` if you want Avatar to add padding around the image
  // and center the padded image inside a gray circle
  paddedImage?: string;
  size?: AvatarSize;
  text?: string;
  className?: string;
  badge?: React.ReactNode;
  onClick?: (event: React.SyntheticEvent) => any;
};

const propsToClasses = {
  color: {
    brand: 'Avatar--brand',
    grey: 'Avatar--grey',
    white: 'Avatar--white',
    fullGrey: 'Avatar--fullGrey',
    coachingBlue: 'Avatar--coachingBlue'
  },
  size: {
    xxlarge: 'Avatar--xxlarge',
    xlarge: 'Avatar--xlarge',
    large: 'Avatar--large',
    medium: 'Avatar--medium',
    small: 'Avatar--small',
    xsmall: 'Avatar--xsmall',
  },
};

/**
 * ## Displays text content in a circle.
 * ### Used primarily for rendering a user's initials (in lieu of a photographic avatar) or a date.
 *
 *
 * ```example
 * <div>
 *   <Avatar
 *     color="brand"
 *     text="XX"
 *     size="small"
 *   />
 *   <Avatar
 *     date={new Date()}
 *     size="medium"
 *   />
 *   <Avatar
 *     color="grey"
 *     text="XX"
 *     size="xsmall"
 *   />
 *   <Avatar
 *     color="fullGrey"
 *     text="XX"
 *     size="small"
 *   />
 * </div>
 * ```
 *
 *
 * # TODO
 * * Consider default `size`, since it needs to be square to look right
 * * Consider more consitent matching of text `color` & `border-color`
 */

declare var __DEV__: string;

export default function Avatar(props: AvatarProps) {
  const {
    className,
    text,
    badge,
    glyphName,
    iconName,
    image,
    paddedImage,
    date,
    size,
    onClick,
  } = props;

  if (__DEV__ && text && text.length > 2) {
    console.warn(`user icon text ${text} should be 2 characters max`);
  }

  return (
    <div
      className={cx('Avatar', getPropClasses(props, propsToClasses), className)}
      onClick={onClick}>
      <AvatarContent
        date={date}
        text={text}
        image={image}
        paddedImage={paddedImage}
        glyphName={glyphName}
        glyphSize={size === 'xsmall' ? 'small' : 'large'}
        iconName={iconName}
        iconSize={size === 'xsmall' ? 'small' : 'large'}
      />
      {badge || null}
    </div>
  );
}
